import React, { useState, useEffect } from 'react';
import { useForm } from 'react-final-form';

import {
  AutocompleteInput,
  BooleanInput,
  Create,
  CheckboxGroupInput,
  FormDataConsumer,
  required,
  SimpleForm,
  TextInput,
  useDataProvider
} from 'react-admin';

import CronTemplateNote from '../widget/cronTemplateNote';

import cronFormatValidator from '../utils/cronFormatValidator'

const FormCatcher = ({ formData, ...rest }) => {
  const form = useForm();

  const dataProvider = useDataProvider();

  const [commandType, setCommandType] = useState('');
  const [posRecords, setPosRecords] = useState([]);
  const [posRecordNames, setPosRecordNames] = useState([]);
  const [posRecordObjects, setPosRecordObjects] = useState({});
  const [cronTemplates, setCronTemplates] = useState('');
  const [cronTemplatesObject, setCronTemplatesObject] = useState({});
  const [companies, setCompanies] = useState([]);
  const [companiesObject, setCompaniesObject] = useState({});
  const [availableCommands, setAvailableCommands] = useState([]);
  const [availableCommandsObject, setAvailableCommandsObject] = useState({});
  const [forceEditCommand, setForceEditCommand] = useState(false);
  const [editableCommand, setEditableCommand] = useState(false);

  const updateChoices = async (companyId) => {
    form.change('posIds', []);

    if (!companyId) {
      setPosRecords([]);
      return;
    }

    if (posRecords) {
      const filter = { companyId };
      const pagination = { page: 1, perPage: 1000 };
      const sort = { field: 'posId', order: 'DESC' };

      await dataProvider
        .getList('pos', { filter, pagination, sort })
        .then(({ data }) => {
          const companyPos = [];
          const companyPosNames = [];
          const companyPosObjects = [];
          data.forEach((value) => {
            companyPos.push(
              {
                id: value.posId,
                name: value.posName
              }
            )

            companyPosNames.push(
              {
                id: value.posName,
                name: value.posId
              }
            )

            companyPosObjects.push(value);
          });

          setPosRecords(companyPos);
          setPosRecordNames(companyPosNames);
          setPosRecordObjects(arrayToObject(companyPosObjects, 'posId'));
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }

  const arrayToObject = (array, keyField) =>
    array.reduce((obj, item) => {
      obj[item[keyField]] = item
      return obj
    }, {})

  useEffect(() => {
    const filter = { enabled: { $ne: false }, followUtc: true };
    const pagination = { page: 1, perPage: 1000 };
    const sort = { field: 'name', order: 'ASC' };

    dataProvider
      .getList('cronTemplates', { filter, pagination, sort })
      .then(({ data }) => {
        setCronTemplates(data);
        setCronTemplatesObject(arrayToObject(data, 'id'));
      })
      .catch((error) => {
        console.log(error);
      });

    dataProvider
      .getCompanies('pos/companies')
      .then(({ data }) => {
        if (data && data.list) {
          const companyList = [];
          data.list.forEach((company) => {
            const { companyId, companyName } = company.id
            companyList.push({
              id: companyId,
              name: companyName
            })
          });

          setCompanies(companyList);
          setCompaniesObject(arrayToObject(companyList, 'id'));
        }
      })
      .catch((error) => {
        console.log(error);
      });

    const filterCommands = {};
    const paginationCommands = { page: 1, perPage: 1000 };
    const sortCommands = { field: 'id', order: 'ASC' };

    const commands = [];
    const commandsObj = [];
    dataProvider
      .getList('commands', {
        filter: filterCommands,
        pagination: paginationCommands,
        sort: sortCommands
      })
      .then(({ data }) => {
        data.forEach((value) => {
          commands.push({
            id: value.id,
            name: value.name,
          });

          commandsObj.push(value);
        });

        setAvailableCommands(commands);
        setAvailableCommandsObject(arrayToObject(commandsObj, 'id'));
      })
      .catch((error) => {
        console.log(error);
      });
  }, [dataProvider]);

  const onChangeCommandType = (cType) => {
    setCommandType(cType);

    if (cType !== 'command') {
      form.change('name', null);
      form.change('command', null);
      setForceEditCommand(false);
    }
  }

  const onChangeCommandTemplate = (commandId) => {
    if (commandId && commandId !== null) {
      setEditableCommand(false);
      form.change('command', availableCommandsObject[commandId].sql);
      form.change('commandName', availableCommandsObject[commandId].name);
      form.change('commandValue', availableCommandsObject[commandId].sql);
    } else {
      setEditableCommand(true);
      form.change('command', '');
      form.change('commandName', '');
      form.change('commandValue', '');
    }
  }

  return (
    <div className='cron-form'>
      <div className="autocompute-field">
        <AutocompleteInput
          source="type"
          validate={[required()]}
          choices={[
            { id: 'backup', name: 'Backup' },
            { id: 'command', name: 'Command' },
          ]}
          onChange={onChangeCommandType}
        />
      </div>

      {
        commandType === 'command' ? (
          <>
            <div className="autocompute-field">
              <AutocompleteInput
                allowEmpty
                source="commandId"
                label="Name"
                choices={availableCommands || []}
                onChange={onChangeCommandTemplate}
                disabled={commandType !== 'command'}
              />
            </div>

            <div>
              <TextInput
                source="command"
                fullWidth
                disabled={!editableCommand && !forceEditCommand}
                multiline
              />
            </div>

            {!editableCommand ? (
              <BooleanInput
                label="Edit selected command"
                source="edit"
                onChange={(e) => {
                  setForceEditCommand(!forceEditCommand);
                }}
              />
            ) : (
                ''
              )}
          </>
        ) : (
            ''
          )
      }

      <div className="autocompute-field">
        <AutocompleteInput
          source="cronId"
          onChange={(e) => {
            form.change('cronName', cronTemplatesObject[e].name);
            form.change('cronExpression', cronTemplatesObject[e].cronExpression);
          }}
          choices={cronTemplates || []} />
      </div>

      <TextInput type='hidden' source="cronName" style={{ display: 'none' }} />

      <div>
        <TextInput
          source="cronExpression"
          label="Cron Expression"
          type="string"
        />
      </div>

      <CronTemplateNote />

      <TextInput type='hidden' source="companyName" style={{ display: 'none' }} />

      <div>
        <div style={{ width: '246px' }}>
          <AutocompleteInput
            source="companyId"
            onChange={(e) => {
              form.change('companyName', companiesObject[e].name);
              form.change('posId', undefined);
              updateChoices(e)
            }} choices={companies || []}
          />
        </div>
      </div>

      <div>
        <CheckboxGroupInput
          source="posIds"
          label="POS Name(s)"
          choices={posRecords}
          validate={[required()]}
          onChange={(e) => {
            const posNames = [];
            e.forEach((posId) => {
              posNames.push(posRecordObjects[posId].posName);
            });

            form.change('posNames', posNames);
          }}
        />
      </div>

      <div className='hidden' style={{ display: 'none' }}>
        <CheckboxGroupInput
          source="posNames"
          label="POS Id(s)"
          choices={posRecordNames}
          validate={[required()]}
          onChange={(e) => {
            const posIds = form.getFieldState('posIds').value;
            const posNames = [];
            posIds.forEach((posId) => {
              posNames.push(posRecordObjects[posId].posName);
            });

            form.change('posNames', posNames);
          }}
        />
      </div>

      <TextInput type='hidden' source="commandId" style={{ display: 'none' }} />
      <TextInput type='hidden' source="commandName" style={{ display: 'none' }} />
      <TextInput type='hidden' source="companyValue" style={{ display: 'none' }} />
    </div>
  )
}

const validator = (values) => {
  const errors = {};

  if (values && values.cronExpression) {
    const isValidCronExpression = cronFormatValidator({ cronExpression: values.cronExpression });
    if (!isValidCronExpression) {
      errors.cronExpression = ['Invalid format'];
    }
  }

  const isCommand = values.type === 'command';
  const isCommandEmpty = ((!values.command) || values.command === '' || values.command === null);

  if (isCommand && isCommandEmpty) {
    errors.command = ['Required'];
  }

  return errors;
};

const CronCreate = (props) => {
  return (
    <Create {...props}>
      <SimpleForm redirect="list" validate={validator}>
        <FormDataConsumer>
          {formDataProps => (
            <FormCatcher {...formDataProps} />
          )}
        </FormDataConsumer>
      </SimpleForm>
    </Create>
  )
};

export default CronCreate;
